import { Injectable } from '@angular/core';

type Alignment = 'left' | 'center' | 'right';
type Sign = 'neg' | 'pos' | 'neutral';

interface Block {
  type: 'table' | 'text';
  lines: string[];
}

const NUMERIC_CELL = /^[\s$()\-+,.\d%]+$/;
const NEGATIVE_CELL = /^\s*\(.+\)\s*$|^\s*-\s*\$?\s*[\d.,]+/;
const POSITIVE_CELL = /^\s*\+/;
const TOTAL_KEYWORDS = /\b(total|subtotal|gran total)\b/i;

@Injectable({ providedIn: 'root' })
export class MarkdownService {
  private readonly cache = new Map<string, string>();
  private readonly maxCacheSize = 200;

  render(raw: string): string {
    if (!raw) return '';
    const cached = this.cache.get(raw);
    if (cached !== undefined) return cached;

    const html = this.renderBlocks(this.toBlocks(raw));
    this.put(raw, html);
    return html;
  }

  private put(key: string, value: string): void {
    if (this.cache.size >= this.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) this.cache.delete(firstKey);
    }
    this.cache.set(key, value);
  }

  private toBlocks(raw: string): Block[] {
    const lines = raw.split('\n');
    const blocks: Block[] = [];
    let i = 0;

    while (i < lines.length) {
      if (this.isTableStart(lines, i)) {
        const tableLines: string[] = [];
        while (i < lines.length && lines[i].trim().startsWith('|')) {
          tableLines.push(lines[i]);
          i++;
        }
        blocks.push({ type: 'table', lines: tableLines });
      } else {
        const textLines: string[] = [];
        while (i < lines.length && !this.isTableStart(lines, i)) {
          textLines.push(lines[i]);
          i++;
        }
        blocks.push({ type: 'text', lines: textLines });
      }
    }

    return blocks;
  }

  private isTableStart(lines: string[], index: number): boolean {
    const current = lines[index]?.trim() ?? '';
    const next = lines[index + 1]?.trim() ?? '';
    if (!current.startsWith('|') || !current.endsWith('|')) return false;
    return /^\|[\s:|-]+\|$/.test(next) && next.includes('-');
  }

  private renderBlocks(blocks: Block[]): string {
    return blocks
      .map(b => (b.type === 'table' ? this.renderTable(b.lines) : this.renderText(b.lines)))
      .join('');
  }

  private renderTable(lines: string[]): string {
    if (lines.length < 2) return this.renderText(lines);

    const headers = this.parseRow(lines[0]);
    const explicitAligns = this.parseRow(lines[1]).map(this.alignmentOf);
    const bodyRows = lines.slice(2).map(l => this.parseRow(l));
    const columnIsNumeric = this.detectNumericColumns(bodyRows, headers.length);

    const resolvedAligns: Alignment[] = headers.map((_, idx) => {
      if (explicitAligns[idx] && explicitAligns[idx] !== 'left') return explicitAligns[idx];
      return columnIsNumeric[idx] ? 'right' : (explicitAligns[idx] ?? 'left');
    });

    const thead =
      '<thead><tr>' +
      headers
        .map(
          (cell, idx) =>
            `<th class="md-align-${resolvedAligns[idx]}">${this.inline(cell)}</th>`
        )
        .join('') +
      '</tr></thead>';

    const tbody =
      '<tbody>' +
      bodyRows.map(row => this.renderBodyRow(row, resolvedAligns, columnIsNumeric)).join('') +
      '</tbody>';

    return `<div class="md-table-wrapper"><table class="md-table">${thead}${tbody}</table></div>`;
  }

  private renderBodyRow(
    row: string[],
    aligns: Alignment[],
    numericCols: boolean[]
  ): string {
    const isTotal = this.isTotalRow(row);
    const tds = row
      .map((cell, idx) => {
        const sign = this.signOf(cell);
        const isNum = numericCols[idx] || NUMERIC_CELL.test(cell.trim());
        const classes = [`md-align-${aligns[idx] ?? 'left'}`];
        if (isNum) classes.push('md-num');
        if (sign === 'neg') classes.push('md-neg');
        if (sign === 'pos') classes.push('md-pos');
        return `<td class="${classes.join(' ')}">${this.inline(cell)}</td>`;
      })
      .join('');
    return isTotal ? `<tr class="md-total-row">${tds}</tr>` : `<tr>${tds}</tr>`;
  }

  private detectNumericColumns(rows: string[][], colCount: number): boolean[] {
    const result: boolean[] = new Array(colCount).fill(false);
    if (rows.length === 0) return result;

    for (let col = 0; col < colCount; col++) {
      let numericHits = 0;
      let nonEmpty = 0;
      for (const row of rows) {
        const cell = (row[col] ?? '').trim();
        if (!cell) continue;
        nonEmpty++;
        const stripped = cell.replace(/<\/?strong>|<\/?em>/g, '').trim();
        if (NUMERIC_CELL.test(stripped)) numericHits++;
      }
      result[col] = nonEmpty > 0 && numericHits / nonEmpty >= 0.6;
    }
    return result;
  }

  private signOf(cell: string): Sign {
    const stripped = cell.replace(/\*\*/g, '').trim();
    if (!stripped) return 'neutral';
    if (NEGATIVE_CELL.test(stripped)) return 'neg';
    if (POSITIVE_CELL.test(stripped)) return 'pos';
    return 'neutral';
  }

  private isTotalRow(row: string[]): boolean {
    const first = (row[0] ?? '').replace(/\*\*/g, '').trim();
    if (!first) return false;
    return TOTAL_KEYWORDS.test(first) || /^\*\*.+\*\*$/.test((row[0] ?? '').trim());
  }

  private renderText(lines: string[]): string {
    const joined = lines.join('\n');
    if (!joined.trim()) return joined.replaceAll('\n', '<br/>');
    return this.inline(joined).replaceAll('\n', '<br/>');
  }

  private parseRow(line: string): string[] {
    const trimmed = line.trim().replace(/^\||\|$/g, '');
    return trimmed.split('|').map(c => c.trim());
  }

  private alignmentOf(cell: string): Alignment {
    const startsColon = cell.startsWith(':');
    const endsColon = cell.endsWith(':');
    if (startsColon && endsColon) return 'center';
    if (endsColon) return 'right';
    return 'left';
  }

  private inline(text: string): string {
    let out = this.escapeHtml(text);
    out = out.replaceAll(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    out = out.replaceAll(/(^|[^*])\*(?!\*)([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>');
    out = out.replaceAll(/`([^`\n]+)`/g, '<code class="md-inline-code">$1</code>');
    return out;
  }

  private escapeHtml(text: string): string {
    return text
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}
